import { on } from "svelte/events";
import { untrack } from "svelte";
import { macrotasks } from "@otto-ec/assets-core-utils/test-utils";
import { OnImmediate } from "./useDomAnimationHelper.awaiter.js";
import type {
  StepsDefinition,
  AnimationHelperAwaiter,
  HelperProcessState,
  CancelAnimation,
  DomAnimationToggleOptions,
} from "./useDomAnimationHelper.types.js";

type NormalizedSteps = [
  forwardSteps: [string, AnimationHelperAwaiter][],
  backwardSteps: [string, AnimationHelperAwaiter][],
];

/**
 *
 */
const ANIMATION_INTERRUPTED = Symbol("ANIMATION_INTERRUPTED");

/**
 *
 *
 *
 *
 *
 *
 *
 *
 */
export type InternalState = HelperProcessState & Record<string, boolean | undefined>;

/**
 *
 *
 *
 *
 *
 */
export const normalizeSteps = (stepsDefinition: StepsDefinition): NormalizedSteps => {
  /*                    */
  const normalizedSteps = Object.entries(stepsDefinition).map(([stepName, stepTriggers]) => {
    /*                                   */
    const triggers = Array.isArray(stepTriggers) ? stepTriggers : [stepTriggers, stepTriggers];

    /*                                                         */
    triggers[0] ??= OnImmediate;
    triggers[1] ??= OnImmediate;

    /*                        */
    return [
      stepName as string,
      triggers as [AnimationHelperAwaiter, AnimationHelperAwaiter],
    ] as const;
  });

  /*                                                                    */
  return [
    /*              */
    normalizedSteps.map((s) => [s[0], s[1][0]]),
    /*                        */
    normalizedSteps.reverse().map((s) => [s[0], s[1][1]]),
  ];
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const getStepsNormalizer =
  (stepsDefinition: StepsDefinition, result?: NormalizedSteps) => (targetValue: boolean) => {
    /*                          */
    /*                                         */
    if (!result) result = normalizeSteps(stepsDefinition);
    /*                                             */
    return targetValue ? result[0] : result[1];
  };

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const setInternalState = (state: InternalState, animIn: boolean | undefined): void => {
  if (animIn === undefined) {
    state.$animIn = false;
    state.$animOut = false;
    state.$direction = undefined;
    delete state.$controller;
    return;
  }

  state.$target = animIn;
  state.$animIn = animIn;
  state.$animOut = !animIn;
  state.$direction = animIn ? "in" : "out";
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const createAnimationStep = (
  state: InternalState,
  [stepName, stepAwaiter]: [string, AnimationHelperAwaiter],
  targetState: boolean,
  { signal }: AbortController,
): (() => Promise<unknown>) => {
  return () =>
    new Promise<unknown>((resolve, reject) => {
      /*                                                                               */
      /*                                                               */
      const unset = on(signal, "abort", () => (cancel?.(), reject(signal.reason)), { once: true });

      /*                                                                              */
      const cancel = stepAwaiter.call({ step: stepName, target: targetState, signal }, () => {
        /*                                                                               */
        /*                                                                      */
        /*                                                 */
        if (signal.aborted) return reject(signal.reason);

        /*                                             */
        resolve((unset(), (state[stepName] = targetState)));
      });
    });
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function processAnimationSteps(
  this: InternalState,
  stepKeys: string[],
  getSteps: ReturnType<typeof getStepsNormalizer>,
  targetState: boolean,
  options: DomAnimationToggleOptions = {},
): CancelAnimation {
  const [currentTarget, currentController] = untrack(() => [this.$target, this.$controller]);

  /*                                                                                */
  /*                                                             */
  if (currentTarget === targetState) return () => {};
  this.$target = targetState;

  /*                                                             */
  if (currentController) currentController.abort(ANIMATION_INTERRUPTED);

  /*                                                              */
  const controller = new AbortController();
  this.$controller = controller;

  /*                                            */
  const [first, ...rest] = getSteps(targetState);
  setInternalState(this, targetState);

  let lastPromise: Promise<unknown>;

  if (options.immediate) {
    /*                                                                 */
    for (const stepName of stepKeys) this[stepName] = targetState;

    /*                                                       */
    lastPromise = macrotasks();
  } else {
    /*                                                    */
    lastPromise = createAnimationStep(this, first!, targetState, controller)();

    /*                          */
    for (const next of rest)
      lastPromise = lastPromise.then(createAnimationStep(this, next, targetState, controller));
  }

  /*                                                            */
  lastPromise.then(
    () => setInternalState(this, undefined),
    (e) => {
      /*                                                                            */
      if (e !== ANIMATION_INTERRUPTED) {
        /*                                                                       */
        /*                                 */
        setInternalState(this, undefined);
        for (const stepName of stepKeys) this[stepName] = !targetState;
      }
    },
  );

  return () => controller.abort();
}
